/**
 * ActionEconomyManager - D&D 5e Action Economy Tracking System
 * 
 * This manager handles proper D&D 5e action economy rules including:
 * - Action (main action): Attack, Cast a Spell, Dash, Disengage, Dodge, Help, Hide, Ready, Search, Use an Object
 * - Bonus Action: Specific spells, off-hand attacks, class features
 * - Movement: Up to speed limit, can be split before/after actions
 * - Reaction: Opportunity attacks, counterspells, reactions (resets at start of turn)
 * - Free Object Interaction: Draw/sheathe weapon, open door, etc. (one per turn)
 * - Free Actions: Speak, drop items, etc. (unlimited)
 * 
 * @date_of_change 2025-08-30
 * - Initial implementation with complete D&D 5e action economy tracking
 */

/**
 * D&D 5e Action Types
 */
export const ACTION_TYPES = {
  ACTION: 'action',                    // Main action (Attack, Spell, Dodge, etc.)
  BONUS_ACTION: 'bonus_action',        // Bonus action (off-hand attack, specific spells)
  MOVEMENT: 'movement',                // Move up to speed
  REACTION: 'reaction',                // Reaction (opportunity attack, counterspell)
  FREE_OBJECT: 'free_object',          // Free object interaction (draw weapon, etc.)
  FREE_ACTION: 'free_action'           // Free action (speak, drop item)
};

/**
 * Standard D&D 5e Actions
 */
export const STANDARD_ACTIONS = {
  // Main Actions
  ATTACK: { type: ACTION_TYPES.ACTION, name: 'Attack', description: 'Make a weapon or spell attack' },
  CAST_SPELL: { type: ACTION_TYPES.ACTION, name: 'Cast a Spell', description: 'Cast a spell with casting time of 1 action' },
  DASH: { type: ACTION_TYPES.ACTION, name: 'Dash', description: 'Gain extra movement equal to your speed' },
  DISENGAGE: { type: ACTION_TYPES.ACTION, name: 'Disengage', description: 'Your movement doesn\'t provoke opportunity attacks' },
  DODGE: { type: ACTION_TYPES.ACTION, name: 'Dodge', description: 'Focus entirely on avoiding attacks' },
  HELP: { type: ACTION_TYPES.ACTION, name: 'Help', description: 'Help another creature with a task or attack' },
  HIDE: { type: ACTION_TYPES.ACTION, name: 'Hide', description: 'Make a Dexterity (Stealth) check' },
  READY: { type: ACTION_TYPES.ACTION, name: 'Ready', description: 'Prepare an action to trigger later' },
  SEARCH: { type: ACTION_TYPES.ACTION, name: 'Search', description: 'Make a Wisdom (Perception) or Intelligence (Investigation) check' },
  USE_OBJECT: { type: ACTION_TYPES.ACTION, name: 'Use an Object', description: 'Interact with a complex object' },

  // Bonus Actions (varies by class/spell)
  OFF_HAND_ATTACK: { type: ACTION_TYPES.BONUS_ACTION, name: 'Off-hand Attack', description: 'Attack with off-hand weapon' },
  BONUS_SPELL: { type: ACTION_TYPES.BONUS_ACTION, name: 'Bonus Action Spell', description: 'Cast a spell with casting time of 1 bonus action' },

  // Movement
  MOVE: { type: ACTION_TYPES.MOVEMENT, name: 'Move', description: 'Move up to your speed' },

  // Reactions (examples)
  OPPORTUNITY_ATTACK: { type: ACTION_TYPES.REACTION, name: 'Opportunity Attack', description: 'Attack when enemy leaves your reach' },
  COUNTERSPELL: { type: ACTION_TYPES.REACTION, name: 'Counterspell', description: 'Attempt to interrupt a spell' },

  // Free Object Interaction
  DRAW_WEAPON: { type: ACTION_TYPES.FREE_OBJECT, name: 'Draw/Sheathe Weapon', description: 'Draw or sheathe a weapon' },
  OPEN_DOOR: { type: ACTION_TYPES.FREE_OBJECT, name: 'Open/Close Door', description: 'Interact with a door or container' },

  // Free Actions
  SPEAK: { type: ACTION_TYPES.FREE_ACTION, name: 'Speak', description: 'Say a few words' },
  DROP_ITEM: { type: ACTION_TYPES.FREE_ACTION, name: 'Drop Item', description: 'Drop an item you\'re holding' }
};

/**
 * Action Economy State for a single combatant
 */
class CombatantActionState {
  constructor(combatantId) {
    this.combatantId = combatantId;
    this._stateRevision = 0;
    this.reset();
  }

  reset() {
    this._stateRevision += 1;
    this.actionsUsed = {
      action: false,                    // Main action used
      bonus_action: false,              // Bonus action used  
      movement: 0,                      // Movement used (in feet)
      reaction: false,                  // Reaction used (resets at start of THEIR turn)
      free_object: false,               // Free object interaction used
      free_action: 0                    // Number of free actions (unlimited but tracked)
    };
    this.actionsThisTurn = [];          // Log of actions taken this turn
    this.featuresUsed = {};             // Per-turn feature usage flags (e.g., sneak_attack)
    this.featureUsageMeta = {};         // Feature usage metadata keyed by featureKey -> { round, turn }
    this.movementLocked = false;
    this.movementLockReason = null;
  }

  hasUsedDashThisTurn() {
    return Array.isArray(this.actionsThisTurn)
      && this.actionsThisTurn.some((action) => (
        action?.name === 'Dash'
        || action?.name === 'Bonus Action Dash'
        || action?.dashUsed === true
      ));
  }

  getEffectiveMovementTotal(speed = 30) {
    const normalizedSpeed = Number.isFinite(Number(speed)) ? Number(speed) : 30;
    return this.hasUsedDashThisTurn() ? (normalizedSpeed * 2) : normalizedSpeed;
  }

  // Check if an action type is available
  canUseAction(actionType, movementCost = 0, speed = 30) {
    switch (actionType) {
      case ACTION_TYPES.ACTION:
        return !this.actionsUsed.action;
      
      case ACTION_TYPES.BONUS_ACTION:
        return !this.actionsUsed.bonus_action;
      
      case ACTION_TYPES.MOVEMENT:
        return !this.movementLocked
          && (this.actionsUsed.movement + movementCost) <= this.getEffectiveMovementTotal(speed);
      
      case ACTION_TYPES.REACTION:
        return !this.actionsUsed.reaction;
      
      case ACTION_TYPES.FREE_OBJECT:
        return !this.actionsUsed.free_object;
      
      case ACTION_TYPES.FREE_ACTION:
        return true; // Always available
      
      default:
        return false;
    }
  }

  // Use an action
  useAction(actionType, actionName, movementCost = 0) {
    const actionLog = {
      type: actionType,
      name: actionName,
      timestamp: new Date().toISOString(),
      movementCost
    };

    switch (actionType) {
      case ACTION_TYPES.ACTION:
        this.actionsUsed.action = true;
        break;
      
      case ACTION_TYPES.BONUS_ACTION:
        this.actionsUsed.bonus_action = true;
        break;
      
      case ACTION_TYPES.MOVEMENT:
        this.actionsUsed.movement += movementCost;
        break;
      
      case ACTION_TYPES.REACTION:
        this.actionsUsed.reaction = true;
        break;
      
      case ACTION_TYPES.FREE_OBJECT:
        this.actionsUsed.free_object = true;
        break;
      
      case ACTION_TYPES.FREE_ACTION:
        this.actionsUsed.free_action += 1;
        break;
      
      default:
        console.warn(`[ActionEconomy] Unknown action type: ${actionType}`);
        break;
    }

    // For Dash action, mark it as used in action history
    if (actionName === 'Dash' || actionName === 'Bonus Action Dash') {
      const dashLog = {
        type: actionType,
        name: actionName,
        timestamp: new Date().toISOString(),
        movementCost,
        dashUsed: true
      };
      this.actionsThisTurn.push(dashLog);
      return dashLog;
    } else {
      this.actionsThisTurn.push(actionLog);
      return actionLog;
    }
  }

  // Get action availability summary
  getActionSummary(speed = 30) {
    const effectiveSpeed = this.getEffectiveMovementTotal(speed);
    const movementTotal = this.movementLocked ? this.actionsUsed.movement : effectiveSpeed;
    return {
      action: {
        available: !this.actionsUsed.action,
        used: this.actionsUsed.action
      },
      bonus_action: {
        available: !this.actionsUsed.bonus_action,
        used: this.actionsUsed.bonus_action
      },
      movement: {
        available: Math.max(0, movementTotal - this.actionsUsed.movement),
        used: this.actionsUsed.movement,
        total: movementTotal,
        locked: this.movementLocked,
        lockReason: this.movementLockReason
      },
      reaction: {
        available: !this.actionsUsed.reaction,
        used: this.actionsUsed.reaction
      },
      free_object: {
        available: !this.actionsUsed.free_object,
        used: this.actionsUsed.free_object
      },
      free_action: {
        available: true,
        used: this.actionsUsed.free_action
      },
      dashUsed: this.hasUsedDashThisTurn()
    };
  }
}

/**
 * Main Action Economy Manager
 */
class ActionEconomyManager {
  constructor() {
    this.combatantStates = new Map();
    this.currentCombatantId = null;
    this.currentRound = 1;
    // @date_of_change 2025-08-31
    // Minimal pub/sub so UI can react to external action economy changes
    this._listeners = new Set();
    // @date_of_change 2025-11-04 - Track active turn to avoid mid-turn resets (rule_reference: PHB initiative order)
    this._activeTurnKey = null;
    this._lastStartedRound = null;
  }

  /**
   * Subscribe to action economy changes.
   * Returns an unsubscribe function.
   * Event shape: { type: 'actionUsed'|'turnStart'|'roundStart'|'reset'|'import', combatantId?, actionType?, actionName?, movementCost?, round? }
   */
  onChange(listener) {
    if (typeof listener === 'function') {
      this._listeners.add(listener);
      return () => this._listeners.delete(listener);
    }
    return () => {};
  }

  // Internal: notify all subscribers of a change
  _emit(event) {
    for (const fn of this._listeners) {
      try {
        fn(event);
      } catch (err) {
        // Never break the manager due to a listener error
        console.error('[ActionEconomy] Listener error', err);
      }
    }
  }

  // Initialize or get combatant action state
  getCombatantState(combatantId) {
    if (!this.combatantStates.has(combatantId)) {
      this.combatantStates.set(combatantId, new CombatantActionState(combatantId));
    }
    return this.combatantStates.get(combatantId);
  }

  // Load action state from combat log for the current turn
  loadActionStateFromLog(combatantId, expectedTurnKey = this._activeTurnKey, turn = null, expectedRevision = null) {
    // Import CombatLogManager dynamically to avoid circular dependency
    import('./CombatLogManager.js').then(({ default: CombatLogManager }) => {
      const logEntries = CombatLogManager.getCombatLog({
        combatantId: combatantId,
        round: this.currentRound,
        ...(Number.isFinite(Number(turn)) ? { turn: Number(turn) } : {})
      });

      if (this._activeTurnKey !== expectedTurnKey) return;
      const state = this.getCombatantState(combatantId);
      const actionsAtLoadStart = state.actionsThisTurn.length;
      const revisionAtLoadStart = expectedRevision ?? state._stateRevision;

      // The log replay is asynchronous. If a canonical action was accepted
      // while it was in flight, never overwrite that newer state with the old
      // replay snapshot. rule_reference: AGENTS.md single source of truth.
      // date_of_change: 2026-07-18
      if (state.actionsThisTurn.length !== actionsAtLoadStart || state._stateRevision !== revisionAtLoadStart) return;

      // Reset state only after confirming no newer action has landed.
      state.reset();

      // Load actions from log and properly accumulate movement
      for (const entry of logEntries) {
        if (entry.type === 'ACTION_STANDARD' || entry.type === 'ACTION_ATTACK' || entry.type === 'ACTION_SPELL') {
          state.actionsUsed.action = true;
        } else if (entry.type === 'ACTION_BONUS') {
          state.actionsUsed.bonus_action = true;
        } else if (entry.type === 'TOKEN_MOVED') {
          // For movement, accumulate the distances
          if (entry.data && entry.data.distance) {
            state.actionsUsed.movement += entry.data.distance;
          }
        }
        // Add more cases for other action types as needed
      }
    }).catch(error => {
      console.error('[ActionEconomyManager] Failed to load action state from log:', error);
    });
  }

  // Start a new turn for a combatant
  startTurn(combatantId, turn = null) {
    const state = this.getCombatantState(combatantId);
    const turnKey = `${this.currentRound}:${combatantId}`;
    const isNewTurn = this._activeTurnKey !== turnKey;

    if (!isNewTurn) {
      return state;
    }

    // @date_of_change 2025-11-04 - Prevent mid-turn action refunds by isolating turn resets to new turn keys
    // rule_reference: AGENTS.md §action_economy_no_fallbacks — never infer action recovery without audited event
    state.reset();
    this.currentCombatantId = combatantId;
    this._activeTurnKey = turnKey;

    // Load action state from combat log for the fresh turn (async replay preserves canonical history)
    this.loadActionStateFromLog(combatantId, turnKey, turn, state._stateRevision);

    this._emit({ type: 'turnStart', combatantId });
    return state;
  }

  // Start a new round
  startRound(round) {
    const nextRound = Math.max(1, Number(round) || 1);
    if (this._lastStartedRound === nextRound) {
      return false;
    }
    this.currentRound = nextRound;
    this._lastStartedRound = nextRound;
    
    // Reset all reactions for all combatants (reactions refresh each round)
    for (const state of this.combatantStates.values()) {
      state.actionsUsed.reaction = false;
    }

    console.log(`[ActionEconomy] Round ${nextRound} started, all reactions reset`);
    this._emit({ type: 'roundStart', round: nextRound });
    return true;
  }

  // Attempt to use an action
  useAction(combatantId, actionType, actionName, options = {}) {
    const state = this.getCombatantState(combatantId);
    const { movementCost = 0, speed = 30 } = options;

    // Check if action is available
    if (!state.canUseAction(actionType, movementCost, speed)) {
      const reason = this.getUnavailableReason(actionType, state, movementCost, speed);
      console.warn(`[ActionEconomy] Cannot use ${actionType} for ${combatantId}: ${reason}`);
      return {
        success: false,
        reason,
        actionSummary: state.getActionSummary(speed)
      };
    }

    // Use the action
    const actionLog = state.useAction(actionType, actionName, movementCost);
    state._stateRevision += 1;
    
    console.log(`[ActionEconomy] ${combatantId} used ${actionType}: ${actionName}`);
    console.log(`[ActionEconomy] Emitting actionUsed event to ${this._listeners.size} listeners`);
    this._emit({ type: 'actionUsed', combatantId, actionType, actionName, movementCost });
    
    return {
      success: true,
      actionLog,
      actionSummary: state.getActionSummary(speed)
    };
  }

  lockMovement(combatantId, reason = 'Movement is unavailable for the rest of this turn') {
    const state = this.getCombatantState(combatantId);
    if (Number(state.actionsUsed?.movement || 0) > 0) {
      return { success: false, reason: 'Movement was already used this turn', actionSummary: state.getActionSummary() };
    }
    state.movementLocked = true;
    state.movementLockReason = reason;
    state._stateRevision += 1;
    this._emit({ type: 'movementLocked', combatantId, reason });
    return { success: true, actionSummary: state.getActionSummary() };
  }

  synchronizeCombatFlags(combatantId, combatFlags = {}, speed = 30) {
    const state = this.getCombatantState(combatantId);
    const hasOwn = (key) => Object.prototype.hasOwnProperty.call(combatFlags || {}, key);
    const hasEconomySnapshot = [
      'actionUsed',
      'bonusActionUsed',
      'reactionUsed',
      'freeObjectUsed',
      'movementUsed',
      'dashUsedThisTurn'
    ].some(hasOwn);

    // When the token carries a persisted economy snapshot, it is authoritative
    // in both directions. A previous true-only merge allowed the singleton UI
    // state to say "Used" after a service refresh had no corresponding token
    // flag, producing split-brain action state.
    // date_of_change: 2026-07-15
    if (hasEconomySnapshot) {
      state.actionsUsed.action = combatFlags.actionUsed === true;
      state.actionsUsed.bonus_action = combatFlags.bonusActionUsed === true;
      state.actionsUsed.reaction = combatFlags.reactionUsed === true;
      state.actionsUsed.free_object = combatFlags.freeObjectUsed === true;
      state.actionsUsed.movement = Number.isFinite(Number(combatFlags.movementUsed))
        ? Math.max(0, Number(combatFlags.movementUsed))
        : 0;
      // Dash identity must survive reconnect alongside movementUsed; otherwise
      // the panel restores a 30-foot ceiling while the token spent from 60.
      // date_of_change: 2026-07-18
      const nonDashActions = state.actionsThisTurn.filter((action) => action?.dashUsed !== true);
      state.actionsThisTurn = combatFlags.dashUsedThisTurn === true
        ? [...nonDashActions, {
            type: combatFlags.bonusActionUsed === true ? ACTION_TYPES.BONUS_ACTION : ACTION_TYPES.ACTION,
            name: combatFlags.bonusActionUsed === true ? 'Bonus Action Dash' : 'Dash',
            dashUsed: true,
            restoredFromSnapshot: true
          }]
        : nonDashActions;
      state._stateRevision += 1;
    }

    if (hasOwn('movementLockedThisTurn') || hasOwn('movementLockReason')) {
      state.movementLocked = combatFlags.movementLockedThisTurn === true;
      state.movementLockReason = state.movementLocked
        ? (combatFlags.movementLockReason || 'Movement is unavailable for the rest of this turn')
        : null;
      // A lock-only canonical update must invalidate an in-flight log replay
      // just like the rest of the economy snapshot. Otherwise the replay can
      // restore movement after Steady Aim has locked it.
      // rule_reference: AGENTS.md — canonical session snapshot is authoritative.
      // date_of_change: 2026-07-18
      if (!hasEconomySnapshot) state._stateRevision += 1;
    }
    return state.getActionSummary(speed);
  }

  /**
   * Calculate distance between two points in pixels and convert to D&D 5e feet
   * @date_of_change 2025-09-06 - MOVEMENT TRACKING IMPLEMENTATION
   * In D&D 5e, 1 square = 5 feet, and grid squares are typically 50 pixels
   * @param {number} fromX - Starting X position in pixels
   * @param {number} fromY - Starting Y position in pixels  
   * @param {number} toX - Ending X position in pixels
   * @param {number} toY - Ending Y position in pixels
   * @param {number} gridSize - Size of grid square in pixels (default 50)
   * @returns {number} Distance in D&D feet (5-foot increments)
   */
  static calculateMovementDistance(fromX, fromY, toX, toY, gridSize = 50) {
    // Calculate pixel distance
    const pixelDistance = Math.sqrt(Math.pow(toX - fromX, 2) + Math.pow(toY - fromY, 2));
    
    // Convert to grid squares (1 square = 5 feet in D&D)
    const gridSquares = pixelDistance / gridSize;
    
    // Round to nearest 5-foot increment (D&D movement is in 5-foot squares)
    const feetDistance = Math.ceil(gridSquares) * 5;
    
    console.log(`[ActionEconomy] Movement calculation:`, {
      fromPos: { x: fromX, y: fromY },
      toPos: { x: toX, y: toY },
      pixelDistance: Math.round(pixelDistance),
      gridSquares: Math.round(gridSquares * 10) / 10,
      feetDistance,
      gridSize
    });
    
    return feetDistance;
  }

  /**
   * Track movement for a combatant by calculating distance moved
   * @date_of_change 2025-09-06 - MOVEMENT TRACKING IMPLEMENTATION
   * @param {string} combatantId - ID of the moving combatant
   * @param {number} fromX - Starting X position
   * @param {number} fromY - Starting Y position
   * @param {number} toX - Ending X position
   * @param {number} toY - Ending Y position
   * @param {number} speed - Character's movement speed in feet (default 30)
   * @param {number} gridSize - Grid size in pixels (default 50)
   * @returns {Object} Movement result with success/failure and remaining movement
   */
  trackMovement(combatantId, fromX, fromY, toX, toY, speed = 30, gridSize = 50) {
    // Calculate distance moved
    const distance = ActionEconomyManager.calculateMovementDistance(fromX, fromY, toX, toY, gridSize);
    
    // If no movement, no need to track
    if (distance === 0) {
      return { success: true, distance: 0, reason: 'No movement' };
    }
    
    // Attempt to use movement
    const result = this.useAction(combatantId, ACTION_TYPES.MOVEMENT, `Move ${distance} feet`, {
      movementCost: distance,
      speed: speed
    });
    
    if (result.success) {
      console.log(`[ActionEconomy] ${combatantId} moved ${distance} feet`);
      return { 
        success: true, 
        distance, 
        // @date_of_change 2025-11-04 - Use canonical remaining movement to avoid double deduction
        // rule_reference: PHB movement budgeting; do not re-subtract beyond CombatantActionState ledger
        remainingMovement: result.actionSummary?.movement?.available ?? Math.max(0, result.actionSummary.movement.total - result.actionSummary.movement.used),
        actionSummary: result.actionSummary
      };
    } else {
      console.warn(`[ActionEconomy] ${combatantId} cannot move ${distance} feet: ${result.reason}`);
      return { 
        success: false, 
        distance, 
        reason: result.reason,
        actionSummary: result.actionSummary
      };
    }
  }

  // Get reason why an action is unavailable
  getUnavailableReason(actionType, state, movementCost, speed) {
    switch (actionType) {
      case ACTION_TYPES.ACTION:
        return 'Main action already used this turn';
      case ACTION_TYPES.BONUS_ACTION:
        return 'Bonus action already used this turn';
      case ACTION_TYPES.MOVEMENT:
        {
          if (state?.movementLocked) {
            return state.movementLockReason || 'Movement is unavailable for the rest of this turn';
          }
          const effectiveSpeed = typeof state?.getEffectiveMovementTotal === 'function'
            ? state.getEffectiveMovementTotal(speed)
            : ((Number.isFinite(Number(speed)) ? Number(speed) : 30));
          const have = Math.max(0, effectiveSpeed - (state.actionsUsed?.movement || 0));
          return `Not enough movement remaining (need ${movementCost}ft, have ${have}ft)`;
        }
      case ACTION_TYPES.REACTION:
        return 'Reaction already used this round';
      case ACTION_TYPES.FREE_OBJECT:
        return 'Free object interaction already used this turn';
      default:
        return 'Unknown action type';
    }
  }

  // Get action summary for a combatant
  getActionSummary(combatantId, speed = 30) {
    const state = this.getCombatantState(combatantId);
    return state.getActionSummary(speed);
  }

  // Get all actions taken this turn by a combatant
  getTurnActions(combatantId) {
    const state = this.getCombatantState(combatantId);
    return state.actionsThisTurn;
  }

  // Mark a named feature as used for this combatant this turn (e.g., 'sneak_attack')
  markFeatureUsed(combatantId, featureKey) {
    if (!featureKey) return false;
    const state = this.getCombatantState(combatantId);
    if (!state.featuresUsed) state.featuresUsed = {};
    state.featuresUsed[featureKey] = true;
    this._emit({ type: 'featureUsed', combatantId, featureKey });
    return true;
  }

  // Check if a named feature has been used by this combatant this turn
  isFeatureUsed(combatantId, featureKey) {
    if (!featureKey) return false;
    const state = this.getCombatantState(combatantId);
    return !!(state.featuresUsed && state.featuresUsed[featureKey]);
  }

  // Return feature usage metadata (e.g., last used round/turn)
  getFeatureUsage(combatantId, featureKey) {
    const state = this.getCombatantState(combatantId);
    return state.featureUsageMeta ? state.featureUsageMeta[featureKey] || null : null;
  }

  // Turn-aware: mark feature as used in the specified round/turn
  markFeatureUsedInTurn(combatantId, featureKey, round, turn) {
    if (!featureKey) return false;
    const state = this.getCombatantState(combatantId);
    if (!state.featureUsageMeta) state.featureUsageMeta = {};
    state.featureUsageMeta[featureKey] = { round: Number(round) || 0, turn: Number(turn) || 0 };
    // Maintain simple flag for UI backward-compatibility
    if (!state.featuresUsed) state.featuresUsed = {};
    state.featuresUsed[featureKey] = true;
    this._emit({ type: 'featureUsed', combatantId, featureKey, round, turn });
    return true;
  }

  // Turn-aware: has feature been used in THIS exact round/turn?
  isFeatureUsedInTurn(combatantId, featureKey, round, turn) {
    const meta = this.getFeatureUsage(combatantId, featureKey);
    if (!meta) return false;
    return (Number(meta.round) === Number(round)) && (Number(meta.turn) === Number(turn));
  }

  // Check if a specific action is available
  canUseAction(combatantId, actionType, options = {}) {
    const state = this.getCombatantState(combatantId);
    const { movementCost = 0, speed = 30 } = options;
    return state.canUseAction(actionType, movementCost, speed);
  }

  // Map common action names to action types
  getActionType(actionName) {
    const actionMap = {
      // Main Actions
      'Attack': ACTION_TYPES.ACTION,
      'Longsword': ACTION_TYPES.ACTION,
      'Shortbow': ACTION_TYPES.ACTION,
      'Bite': ACTION_TYPES.ACTION,
      'Claw': ACTION_TYPES.ACTION,
      'Dash': ACTION_TYPES.ACTION,
      'Disengage': ACTION_TYPES.ACTION,
      'Dodge': ACTION_TYPES.ACTION,
      'Help': ACTION_TYPES.ACTION,
      'Hide': ACTION_TYPES.ACTION,
      'Ready': ACTION_TYPES.ACTION,
      'Search': ACTION_TYPES.ACTION,
      'Use an Object': ACTION_TYPES.ACTION,

      // Spells (check if bonus action spell)
      'Fire Bolt': ACTION_TYPES.ACTION,
      'Healing Word': ACTION_TYPES.BONUS_ACTION,
      'Spiritual Weapon': ACTION_TYPES.BONUS_ACTION,
      
      // Movement
      'Move': ACTION_TYPES.MOVEMENT,
      'Move 30ft': ACTION_TYPES.MOVEMENT,

      // Bonus Actions
      'Second Wind': ACTION_TYPES.BONUS_ACTION,
      'Off-hand Attack': ACTION_TYPES.BONUS_ACTION,
      'Bonus Action Dash': ACTION_TYPES.BONUS_ACTION,

      // Free Actions
      'Speak': ACTION_TYPES.FREE_ACTION,
      'Drop Item': ACTION_TYPES.FREE_ACTION,

      // Free Object Interaction
      'Draw Weapon': ACTION_TYPES.FREE_OBJECT,
      'Sheathe Weapon': ACTION_TYPES.FREE_OBJECT
    };

    return actionMap[actionName] || ACTION_TYPES.ACTION; // Default to main action
  }

  // Reset all combatant states (new combat)
  resetAllStates() {
    this.combatantStates.clear();
    this.currentCombatantId = null;
    this.currentRound = 1;
    // Reset must clear the active turn key or a new combat can inherit stale
    // idempotency state from the previous encounter. date_of_change: 2026-06-23
    this._activeTurnKey = null;
    this._lastStartedRound = null;
    // Notify listeners so UI panels (ActionTracker) refresh immediately
    this._emit({ type: 'reset' });
  }

  /**
   * Export action economy state for session save
   * Keeps a compact shape to avoid bloating session data.
   */
  exportState() {
    try {
      const states = {};
      for (const [id, state] of this.combatantStates.entries()) {
        states[id] = {
          actionsUsed: { ...state.actionsUsed },
          featuresUsed: { ...(state.featuresUsed || {}) },
          featureUsageMeta: { ...(state.featureUsageMeta || {}) },
          movementLocked: Boolean(state.movementLocked),
          movementLockReason: state.movementLockReason || null
        };
      }
      return {
        version: 1,
        currentRound: this.currentRound,
        currentCombatantId: this.currentCombatantId,
        activeTurnKey: this._activeTurnKey,
        states
      };
    } catch (e) {
      console.error('[ActionEconomy] exportState failed:', e);
      return { version: 1, currentRound: 1, currentCombatantId: null, activeTurnKey: null, states: {} };
    }
  }

  /**
   * Import action economy state from session load
   */
  importState(payload) {
    try {
      if (!payload || typeof payload !== 'object') return;
      this.combatantStates.clear();
      const states = payload.states || {};
      for (const id of Object.keys(states)) {
        const s = this.getCombatantState(id);
        const src = states[id] || {};
        // Reset then apply
        s.reset();
        if (src.actionsUsed && typeof src.actionsUsed === 'object') {
          s.actionsUsed = { ...s.actionsUsed, ...src.actionsUsed };
        }
        if (src.featuresUsed && typeof src.featuresUsed === 'object') {
          s.featuresUsed = { ...src.featuresUsed };
        }
        if (src.featureUsageMeta && typeof src.featureUsageMeta === 'object') {
          s.featureUsageMeta = { ...src.featureUsageMeta };
        }
        s.movementLocked = Boolean(src.movementLocked);
        s.movementLockReason = src.movementLockReason || null;
      }
      this.currentRound = Number(payload.currentRound) || 1;
      // Import is already an authoritative snapshot for this round. Mounting a
      // presentation panel must not replay the round transition.
      // date_of_change: 2026-07-27
      this._lastStartedRound = this.currentRound;
      this.currentCombatantId = payload.currentCombatantId || null;
      this._activeTurnKey = payload.activeTurnKey || null;
      // Notify listeners so UI panels (ActionTracker) refresh immediately
      this._emit({ type: 'import' });
    } catch (e) {
      console.error('[ActionEconomy] importState failed:', e);
    }
  }
}

// Create singleton instance
const actionEconomyManager = new ActionEconomyManager();

// @date_of_change 2025-08-30
// Lint/build fix: Avoid duplicate export of STANDARD_ACTIONS. It's already exported above as
// a named const. Re-exporting it again in the export list triggers a duplicate export error in
// our tooling. Keep class exports and default singleton export only.
export { ActionEconomyManager, CombatantActionState };
export default actionEconomyManager;
